<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="pa-4">
    <!-- Status summary -->
    <v-card outlined class="mb-4">
      <v-card-text class="d-flex flex-wrap align-center" style="gap: 12px;">
        <div>
          <div class="caption grey--text">Status połączenia</div>
          <v-chip
            small
            :color="status.configured ? (status.lastTestStatus === 'OK' ? 'success' : status.lastTestStatus === 'FAIL' ? 'error' : 'warning') : 'grey'"
            text-color="white"
            class="mt-1"
          >
            <v-icon x-small left>{{ status.configured ? 'mdi-lan-connect' : 'mdi-lan-disconnect' }}</v-icon>
            {{ statusLabel }}
          </v-chip>
        </div>
        <v-divider vertical />
        <div>
          <div class="caption grey--text">Auto-import (cron)</div>
          <v-chip
            small
            :color="status.autoImportEnabled ? 'success' : 'grey'"
            text-color="white"
            class="mt-1"
          >
            <v-icon x-small left>{{ status.autoImportEnabled ? 'mdi-check' : 'mdi-pause' }}</v-icon>
            {{ status.autoImportEnabled ? 'Włączony' : 'Wyłączony' }}
          </v-chip>
        </div>
        <v-divider vertical />
        <div>
          <div class="caption grey--text">Ostatni test</div>
          <div class="text-body-2">
            <span v-if="status.lastTestedAt">{{ formatDate(status.lastTestedAt) }}</span>
            <span v-else class="grey--text">—</span>
          </div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('refresh')" :loading="loading" title="Odśwież status">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Credentials form -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        <v-icon left>mdi-key-variant</v-icon>
        Dane dostępu API
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="status.clientId"
              label="Client ID"
              outlined dense
              :placeholder="status.clientIdMasked || ''"
              :persistent-placeholder="!!status.clientIdMasked"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="status.clientSecret"
              label="Client Secret"
              :placeholder="status.configured ? '••••••••' : ''"
              type="password" outlined dense
              autocomplete="new-password"
            />
          </v-col>
        </v-row>
        <v-row v-if="status.configured">
          <v-col cols="12" md="6" class="py-0">
            <div class="caption grey--text">
              External company ID: <strong>{{ status.externalCompanyId || '—' }}</strong>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col class="d-flex align-center" style="gap: 8px;">
            <v-btn color="primary" @click="$emit('save')" :loading="status.saving"
                   :disabled="!status.clientId || (!status.clientSecret && !status.configured)">
              <v-icon left small>mdi-content-save</v-icon> Zapisz
            </v-btn>
            <v-btn outlined color="info" @click="$emit('test')" :loading="status.testing" :disabled="!status.configured">
              <v-icon left small>mdi-lan-connect</v-icon> Testuj połączenie
            </v-btn>
            <v-spacer />
            <v-btn v-if="status.configured" small outlined color="error" @click="$emit('remove')" :loading="status.removing">
              <v-icon small left>mdi-delete-outline</v-icon> Usuń dane
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Auto-import -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        <v-icon left>mdi-calendar-sync</v-icon>
        Auto-import tygodniowy
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="d-flex align-center">
          <v-switch
            :input-value="status.autoImportEnabled"
            :label="status.autoImportEnabled ? 'Cron włączony — import w każdy poniedziałek 8:00 PL' : 'Cron wyłączony — brak automatycznego importu'"
            hide-details
            dense
            :disabled="!status.configured"
            @change="$emit('toggle-auto-import', $event)"
          />
        </div>
        <div class="caption grey--text mt-3">
          Po starcie backendu odpala się catch-up jeśli poprzedni poniedziałek został pominięty.
        </div>
      </v-card-text>
    </v-card>

    <!-- Ostatnie uruchomienia crona -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        <v-icon left>mdi-history</v-icon>
        Ostatnie uruchomienia crona
        <v-spacer />
        <v-btn icon small @click="$emit('refresh-jobs')" :loading="status.jobsLoading">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-simple-table dense v-if="(status.jobs || []).length">
        <thead>
          <tr>
            <th>Uruchomiono</th>
            <th>Status</th>
            <th>Zakończono</th>
            <th class="text-right">Czas (s)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="j in status.jobs" :key="j.id">
            <td>{{ formatDate(j.createdAt) }}</td>
            <td><v-chip x-small :color="jobColor(j.status)" text-color="white">{{ j.status }}</v-chip></td>
            <td>{{ j.finishedAt ? formatDate(j.finishedAt) : '—' }}</td>
            <td class="text-right">{{ jobDuration(j) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <div v-else class="pa-4 caption grey--text text-center">Brak uruchomień crona w historii.</div>
    </v-card>

    <!-- Ręczny import -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        <v-icon left>mdi-play-circle</v-icon>
        Ręczny import (poza cronem)
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field v-model="status.manualFrom" label="Od (YYYY-MM-DD)" type="date" outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="status.manualTo" label="Do (YYYY-MM-DD)" type="date" outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="status.manualEquationId"
              :items="status.equations || []"
              item-text="name" item-value="id"
              label="Wzór (współpraca)"
              outlined dense hide-details="auto"
              no-data-text="Brak wzorów"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex">
            <v-btn
              color="primary" block
              :loading="status.manualRunning"
              :disabled="!status.configured || !status.manualFrom || !status.manualTo || !status.manualEquationId"
              @click="$emit('manual-run')"
            >
              <v-icon left small>mdi-play</v-icon>Uruchom
            </v-btn>
          </v-col>
        </v-row>
        <div class="caption grey--text mt-3">
          Pobiera dane z API {{ provider }} dla wybranego zakresu i wzoru. Powstaje wstępny import widoczny w „Historia importu".
        </div>
      </v-card-text>
    </v-card>

    <!-- Powiązane narzędzia -->
    <v-card outlined>
      <v-card-title class="text-subtitle-1">
        <v-icon left>mdi-link-variant</v-icon>
        Powiązane narzędzia
      </v-card-title>
      <v-divider />
      <slot name="related" />
    </v-card>
  </div>
</template>

<script>
// Świadomie mutujemy obiekt `status` z parent-a (form-bind przez v-model na
// polach takich jak status.clientId / status.manualFrom). To anti-pattern Vue
// w teorii, ale w praktyce parent traktuje status jako referencję na pełny
// stan integracji per provider (bolt/uber) i te mutacje są oczekiwane —
// alternatywą byłoby emit per pole, co nie wnosi tu wartości.
/* eslint-disable vue/no-mutating-props */
export default {
  name: "ProviderTabBody",
  props: {
    provider: { type: String, required: true },
    status: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  computed: {
    statusLabel() {
      if (!this.status.configured) return 'Niepołączone';
      if (this.status.lastTestStatus === 'OK') return 'Połączone — test OK';
      if (this.status.lastTestStatus === 'FAIL') return 'Połączone — test ZAWIÓDŁ';
      return 'Połączone — brak testu';
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      return dt.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
    },
    jobColor(s) {
      return ({ DONE: 'success', RUNNING: 'info', FAILED: 'error', ERROR: 'error' })[s] || 'grey';
    },
    jobDuration(j) {
      if (!j.finishedAt || !j.createdAt) return '—';
      const ms = new Date(j.finishedAt).getTime() - new Date(j.createdAt).getTime();
      return (ms / 1000).toFixed(1);
    },
  },
};
</script>
